<script>
import {computed, defineComponent, onBeforeMount, reactive, ref, watch} from "vue";
import {useStore} from "vuex";

import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Textarea from 'primevue/textarea';
import MainCard from "@/components/cards/MainCard";
import Breadcrumb from "@/components/Breadcrumb";
import Skeleton from "primevue/skeleton"
import {useI18n} from "vue-i18n";
import {useRoute, useRouter} from "vue-router";
import {required} from "@/utils/i18n-validators";
import {useVuelidate} from "@vuelidate/core";
import {useApiCall} from "@/composables/useApiCall";
import {
  hintsApiCall,
  updateHintsItemApiCall,
} from "@/services/settings";

export default defineComponent({
  layout: {name: 'AdminLayout'},
  components: {
    Button,
    InputText,
    MainCard,
    Breadcrumb,
    Textarea,
    Skeleton,
  },
  setup() {
    const {t} = useI18n();

    const store = useStore();
    const lang = computed(() => store.getters.getLang);
    const currentKey = computed(() => `${route.params.key}_${lang.value}`);
    const titleField = computed(() => {
      switch (route.params.key) {
        case 'total_income':
          return 'Общая сумма доходов по всем объектам в управлении УК'
        case 'total_deduction':
          return 'Общая сумма доходов по всем объектам в управлении УК'
        case 'total_expense':
          return 'Общая сумма расходов по всем объектам'
        case 'total_to_pay':
          return 'Общая сумма дохода к выплате по всем объектам'
      }
    })

    const formField = reactive({
      fields: {
        total_deduction_ch: '',
        total_deduction_en: '',
        total_deduction_ru: '',
        total_expense_ch: '',
        total_expense_en: '',
        total_expense_ru: '',
        total_income_ch: '',
        total_income_en: '',
        total_income_ru: '',
        total_to_pay_ch: '',
        total_to_pay_en: '',
        total_to_pay_ru: '',
      }
    })

    const router = useRouter()
    const route = useRoute()
    const errors = ref(null)
    const breadcrumbs = ref([]);
    const isContentLoading = ref(true);
    const {
      isLoading: isContentUpdateLoading,
      error: contentUpdateError,
      executeApiCall: updateContentAction,
    } = useApiCall(updateHintsItemApiCall, true)

    const update = async () => {
      errors.value = null
      try {
        await updateContentAction(formField.fields)
        await router.push({name: 'settings', query: {tab: 'hints'}});
      } catch (e) {
        console.error(e)
        if (contentUpdateError.value) {
          errors.value = contentUpdateError.value.data
        }
      }
    }
    const {
      data: hintsData,
      executeApiCall: getHintsAction,
    } = useApiCall(hintsApiCall, true)

    onBeforeMount(async () => {
      await getHintsAction()
      if (hintsData.value) {
        for (const key in hintsData.value.data) {
          if (hintsData.value.data[key]) {
            formField.fields[key] = hintsData.value.data[key]
          }
        }
      }
      breadcrumbs.value = [
        {label: 'Настройки / Пояснения к графикам отчетов', router: {name: 'settings', query: {tab: 'hints'}}},
        {label: 'Редактировать'}
      ];
      isContentLoading.value = false
    });

    return {
      t,
      formField,
      breadcrumbs,
      route,
      update,
      errors,
      isContentLoading,
      titleField,
      isContentUpdateLoading,
      currentKey,
    };
  }
});
</script>

<template>
  <section class="py-2 mb-3">
    <div class="flex justify-content-between align-items-center">
      <Breadcrumb :data="breadcrumbs" separator="/"/>
      <div class="flex flex-wrap gap-4">
        <Button
            @click="update"
            :loading="!!isContentUpdateLoading"
            label="Сохранить"
            class="modals-item__save-button ml-auto"
        />
      </div>
    </div>
  </section>

  <section class="py-2 mb-3">
    <Skeleton v-if="isContentLoading" height="154px"></Skeleton>
    <MainCard v-else title="Наименование графика">
      <span class="p-float-label w-full">
        <InputText
            id="name_ru"
            class="w-full"
            v-model="titleField"
            disabled
        />
        <label for="name_ru">Наименование</label>
      </span>
    </MainCard>
  </section>

  <section class="py-2 mb-3">
    <Skeleton v-if="isContentLoading" height="195px"></Skeleton>
    <MainCard v-else title="Пояснение">
      <div class="grid">
        <div class="col-12">
          <span class="p-float-label w-full">
              <Textarea
                  :key="currentKey"
                  :class="{'p-invalid': errors?.[currentKey] }"
                  id="name_ru"
                  class="w-full"
                  rows="7"
                  v-model="formField.fields[currentKey]"
              />
              <label for="name_ru">Введите пояснение</label>
            </span>
          <span v-if="errors?.[currentKey]"
                class="color-error mt-3">{{ errors[currentKey][0] }}</span>
        </div>
      </div>
    </MainCard>
  </section>
</template>

<style lang="scss" scoped>
.modals-item__save-button {
  border: 1px solid #C3987A;
  color: white;
  background-color: #C3987A;
  font-weight: 400;
  font-size: 13px;
  line-height: 22px;
  border-radius: 4px;
  padding: 12px 24px;
  cursor: pointer;
  @media screen and (min-width: 1600px) {
    padding: 12px 85px;
  }
  &:hover {
    background-color: #C3987A !important;
  }
}

::v-deep(.text-3xl) {
  font-weight: 600;
  font-size: 24px !important;
  line-height: 32px;
}
</style>